import { useMemo, useRef, useState, useCallback, useEffect } from "react";

const STAGE_CFG = {
  awake:       { label: "Awake", color: "#FFB74D", statKey: "awakeSec" },
  rem_sleep:   { label: "REM",   color: "#C084FC", statKey: "rapidSec" },
  light_sleep: { label: "Light", color: "#A78BFA", statKey: "shallowSec" },
  deep_sleep:  { label: "Deep",  color: "#6366F1", statKey: "deepSec" },
};

// Top-to-bottom row order for both the chart lanes and the y-axis labels.
const STAGE_ORDER = ["awake", "rem_sleep", "light_sleep", "deep_sleep"];
const ROW_HEIGHT_PCT = 100 / STAGE_ORDER.length;

const YAXIS_WIDTH = 52;
const CHART_LABEL_PAD = 4;
const MIN_ZOOM = 1;
const MAX_ZOOM = 60; // 60x = down to individual seconds
const DRAG_PX_THRESHOLD = 4; // below this, treat mouseup as a "tap" not a drag

function formatTime(ms, withSeconds = false) {
  if (!ms) return "";
  return new Date(ms).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    ...(withSeconds ? { second: "2-digit" } : {}),
    hour12: true,
  });
}

function formatDuration(sec) {
  if (!sec) return "0m";
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// Picks a readable tick interval based on how much time is currently
// visible, so zooming in reveals finer (down to per-second) ticks and
// zooming out collapses back to coarse hour ticks.
function buildTicks(visStartMs, visEndMs, trackWidthPx) {
  const spanMs = visEndMs - visStartMs;
  if (!trackWidthPx || spanMs <= 0) return [];
  const minPxGap = 60;
  const maxTicks = Math.max(1, Math.floor(trackWidthPx / minPxGap));
  const candidates = [
    1000, 5000, 10000, 15000, 30000,
    60000, 2 * 60000, 5 * 60000, 10 * 60000, 15 * 60000, 30 * 60000,
    60 * 60000, 2 * 60 * 60000, 3 * 60 * 60000,
  ];
  const interval = candidates.find(c => Math.floor(spanMs / c) <= maxTicks)
    || candidates[candidates.length - 1];

  const ticks = [];
  const first = Math.ceil(visStartMs / interval) * interval;
  for (let t = first; t <= visEndMs; t += interval) {
    ticks.push({
      ms: t,
      pct: ((t - visStartMs) / spanMs) * 100,
      label: formatTime(t, interval < 60000),
    });
  }
  return ticks;
}

// ─── SleepGraph ───────────────────────────────────────────────────────────────
// sleepDayData shape: { segments, sleepStart, sleepEnd, stats }
export default function SleepGraph({ sleepDayData, dateControl }) {
  const segments = sleepDayData?.segments || [];
  const viewStart = sleepDayData?.sleepStart;
  const viewEnd = sleepDayData?.sleepEnd;
  const stats = sleepDayData?.stats;
  const fullSpanMs = (viewEnd - viewStart) || 1;
  const hasData = segments.length > 0 && viewStart && viewEnd;

  // ── Zoom / pan state ──────────────────────────────────────────────────────
  const [zoom, setZoom] = useState(1);       // 1 = fully zoomed out
  const [panFrac, setPanFrac] = useState(0); // 0 = leftmost, fraction of full range
  const [hovered, setHovered] = useState(null);
  const [trackW, setTrackW] = useState(0);

  const trackRef = useRef(null);
  const dragRef = useRef({ active: false, moved: false, startX: 0, startPan: 0 });
  const pinchRef = useRef({ active: false, startDist: 0, startZoom: 1, startPanFrac: 0, midFrac: 0 });

  useEffect(() => {
    if (!trackRef.current) return;
    const ro = new ResizeObserver(([e]) => setTrackW(Math.floor(e.contentRect.width)));
    ro.observe(trackRef.current);
    return () => ro.disconnect();
  }, []);

  // Reset the view whenever a new day's data comes in.
  useEffect(() => {
    setZoom(1);
    setPanFrac(0);
    setHovered(null);
  }, [viewStart, viewEnd]);

  const clampPan = useCallback((p, z) => {
    const windowFrac = 1 / z;
    return Math.max(0, Math.min(1 - windowFrac, p));
  }, []);

  const windowFrac = 1 / zoom;
  const clampedPan = clampPan(panFrac, zoom);
  const visStartMs = viewStart + clampedPan * fullSpanMs;
  const visEndMs = visStartMs + windowFrac * fullSpanMs;
  const visSpanMs = visEndMs - visStartMs || 1;
  const isZoomed = zoom > 1.01;

  // Segments positioned against the *visible* window, so panning/zooming
  // just re-maps the same segment list onto a different slice of time.
  const visibleBars = useMemo(() => {
    if (!hasData) return [];
    return segments
      .filter(seg => seg.endMs > visStartMs && seg.startMs < visEndMs)
      .map(seg => {
        const cfg = STAGE_CFG[seg.stage];
        const startPct = ((seg.startMs - visStartMs) / visSpanMs) * 100;
        const widthPct = ((seg.endMs - seg.startMs) / visSpanMs) * 100;
        const rowIndex = STAGE_ORDER.indexOf(seg.stage);
        return { seg, cfg, startPct, widthPct, rowIndex };
      });
  }, [segments, hasData, visStartMs, visEndMs, visSpanMs]);

  const ticks = useMemo(() => {
    if (!hasData) return [];
    return buildTicks(visStartMs, visEndMs, trackW);
  }, [hasData, visStartMs, visEndMs, trackW]);

  // ── Wheel zoom (centered on cursor) ───────────────────────────────────────
  const handleWheel = useCallback((e) => {
    if (!hasData || !trackW) return;
    e.preventDefault();
    const rect = trackRef.current.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const cursorFrac = clampedPan + (px / trackW) * windowFrac;

    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom * delta));
    const newWinFrac = 1 / newZoom;
    const newPan = clampPan(cursorFrac - (px / trackW) * newWinFrac, newZoom);

    setZoom(newZoom);
    setPanFrac(newPan);
    setHovered(null);
  }, [hasData, trackW, clampedPan, windowFrac, zoom, clampPan]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  // ── Mouse drag to pan ──────────────────────────────────────────────────────
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0 || !isZoomed) return;
    dragRef.current = { active: true, moved: false, startX: e.clientX, startPan: clampedPan };
  }, [isZoomed, clampedPan]);

  const handleMouseMove = useCallback((e) => {
    if (dragRef.current.active) {
      const dx = e.clientX - dragRef.current.startX;
      if (Math.abs(dx) > DRAG_PX_THRESHOLD) dragRef.current.moved = true;
      const dFrac = -(dx / trackW) * windowFrac;
      setPanFrac(clampPan(dragRef.current.startPan + dFrac, zoom));
      setHovered(null);
      return;
    }
    if (!hasData || !trackW || !visibleBars.length) return;
    const rect = trackRef.current.getBoundingClientRect();
    const px = Math.max(0, Math.min(trackW, e.clientX - rect.left));
    const pct = (px / trackW) * 100;
    const hit = visibleBars.find(b => pct >= b.startPct && pct < b.startPct + b.widthPct);
    setHovered(hit ? { ...hit, xPct: pct } : null);
  }, [trackW, windowFrac, zoom, clampPan, hasData, visibleBars]);

  const handleMouseUp = useCallback(() => {
    dragRef.current.active = false;
  }, []);

  const handleMouseLeave = useCallback(() => {
    dragRef.current.active = false;
    setHovered(null);
  }, []);

  // ── Touch: single-finger pan, two-finger pinch zoom ───────────────────────
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      const [t1, t2] = e.touches;
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const midX = (t1.clientX + t2.clientX) / 2;
      const rect = trackRef.current.getBoundingClientRect();
      const relX = midX - rect.left;
      const midFrac = clampedPan + (relX / trackW) * windowFrac;
      pinchRef.current = { active: true, startDist: dist, startZoom: zoom, startPanFrac: clampedPan, midFrac };
    } else if (e.touches.length === 1 && isZoomed) {
      dragRef.current = { active: true, moved: false, startX: e.touches[0].clientX, startPan: clampedPan };
      setHovered(null);
    }
  }, [zoom, clampedPan, windowFrac, trackW, isZoomed]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2 && pinchRef.current.active) {
      e.preventDefault();
      const [t1, t2] = e.touches;
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const scale = dist / pinchRef.current.startDist;
      const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, pinchRef.current.startZoom * scale));
      const newWinFrac = 1 / newZoom;
      const midX = (t1.clientX + t2.clientX) / 2;
      const rect = trackRef.current.getBoundingClientRect();
      const relX = midX - rect.left;
      const newPan = clampPan(pinchRef.current.midFrac - (relX / trackW) * newWinFrac, newZoom);
      setZoom(newZoom);
      setPanFrac(newPan);
    } else if (e.touches.length === 1 && dragRef.current.active) {
      const dx = e.touches[0].clientX - dragRef.current.startX;
      if (Math.abs(dx) > DRAG_PX_THRESHOLD) dragRef.current.moved = true;
      const dFrac = -(dx / trackW) * windowFrac;
      setPanFrac(clampPan(dragRef.current.startPan + dFrac, zoom));
    }
  }, [trackW, windowFrac, zoom, clampPan]);

  const handleTouchEnd = useCallback((e) => {
    if (e.touches.length < 2) pinchRef.current.active = false;
    if (e.touches.length === 0) dragRef.current.active = false;
  }, []);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPanFrac(0);
    setHovered(null);
  }, []);

  const tooltipLeftPct = hovered
    ? Math.max(6, Math.min(94, hovered.startPct + hovered.widthPct / 2))
    : 0;

  return (
    <div className="allgraph-container">
      <div className="graph-date-bar">{dateControl}</div>

      {/* ── Summary row: total sleep + per-stage durations ── */}
      {hasData && stats && (
        <div style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 18,
          padding: "6px 10px 14px",
        }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>
              {formatDuration(stats.totalSec)}
            </div>
            <div style={{ fontSize: 11, color: "#64748b" }}>Total Sleep</div>
          </div>

          {STAGE_ORDER.map(stage => {
            const cfg = STAGE_CFG[stage];
            const sec = stats[cfg.statKey] || 0;
            return (
              <div key={stage} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: cfg.color, display: "inline-block" }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{formatDuration(sec)}</div>
                  <div style={{ fontSize: 10, color: "#64748b" }}>{cfg.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Zoom hint + reset ── */}
      {hasData && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10,
          padding: "0 10px 6px", fontSize: 11, color: "#94a3b8",
        }}>
          <span>{isZoomed ? `${zoom.toFixed(1)}x — drag to pan` : "Scroll or pinch to zoom"}</span>
          {isZoomed && (
            <button
              type="button"
              onClick={resetZoom}
              style={{
                fontSize: 11, padding: "2px 8px", borderRadius: 6,
                border: "1px solid #e2e8f0", background: "#fff", color: "#475569", cursor: "pointer",
              }}
            >
              Reset
            </button>
          )}
        </div>
      )}

      {/* ── Chart: y-axis labels + track live side-by-side in a flex row,
          so nothing can silently overlap/cover the labels. ── */}
      <div style={{ display: "flex", alignItems: "stretch" }}>
        <div
          style={{
            position: "relative",
            width: YAXIS_WIDTH,
            flex: `0 0 ${YAXIS_WIDTH}px`,
            height: 160,
          }}
        >
          {hasData ? (
            STAGE_ORDER.map((stage, i) => (
              <span
                key={stage}
                style={{
                  position: "absolute",
                  right: 8,
                  top: `${i * ROW_HEIGHT_PCT}%`,
                  height: `${ROW_HEIGHT_PCT}%`,
                  display: "flex",
                  alignItems: "center",
                  fontSize: 11,
                  fontWeight: 600,
                  color: STAGE_CFG[stage].color,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {STAGE_CFG[stage].label}
              </span>
            ))
          ) : (
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center" }}>
              <span style={{ fontSize: 20 }}>🌙</span>
              <span style={{ marginLeft: 6, fontSize: 11, color: "#64748b" }}>Sleep</span>
            </div>
          )}
        </div>

        <div style={{ position: "relative", flex: 1, height: 160 }}>
          <div
            ref={trackRef}
            style={{
              position: "relative",
              height: "100%",
              borderRadius: 8,
              overflow: "hidden",
              background: "#f8fafc",
              border: "1px solid #eef0f4",
              cursor: hasData ? (isZoomed ? "grab" : "crosshair") : "default",
              touchAction: hasData ? "none" : "auto",
              userSelect: "none",
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {hasData ? (
              <>
                {/* Horizontal row separators */}
                {STAGE_ORDER.map((_, i) => (
                  <div key={`row-${i}`} style={{
                    position: "absolute", left: 0, right: 0,
                    top: `${i * ROW_HEIGHT_PCT}%`, height: "1px", background: "#e2e8f0",
                  }} />
                ))}
                <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: "1px", background: "#e2e8f0" }} />

                {/* Vertical gridlines, zoom-aware */}
                {ticks.map(tick => (
                  <div key={`grid-${tick.ms}`} style={{
                    position: "absolute", top: 0, bottom: 0, left: `${tick.pct}%`,
                    width: "1px", background: "#eef2f7",
                  }} />
                ))}

                {/* Sleep-stage segments, mapped onto the visible window */}
                {visibleBars.map((b, i) => (
                  <div key={i}
                    style={{
                      position: "absolute",
                      left: `${Math.max(0, b.startPct)}%`,
                      width: `${Math.max(0.2, b.widthPct)}%`,
                      top: `${Math.max(0, b.rowIndex) * ROW_HEIGHT_PCT}%`,
                      height: `${ROW_HEIGHT_PCT}%`,
                      background: b.cfg?.color || "#A78BFA",
                      opacity: hovered && hovered.seg !== b.seg ? 0.45 : 0.9,
                      borderRadius: 2,
                      transition: "opacity 120ms ease",
                    }}
                  />
                ))}

                {/* Hover crosshair */}
                {hovered && (
                  <div style={{
                    position: "absolute", top: 0, bottom: 0, left: `${hovered.xPct}%`,
                    width: "1px", background: "rgba(99,102,241,0.35)",
                  }} />
                )}
              </>
            ) : (
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                height: "100%", fontSize: 12, color: "#9ca3af",
              }}>No sleep data for this day</div>
            )}

            {/* Tooltip */}
            {hovered && (
              <div style={{
                position: "absolute",
                left: `${tooltipLeftPct}%`,
                top: 6,
                transform: "translateX(-50%)",
                background: "#0f172a",
                color: "#fff",
                fontSize: 11,
                padding: "4px 8px",
                borderRadius: 6,
                whiteSpace: "nowrap",
                pointerEvents: "none",
                boxShadow: "0 4px 10px rgba(15,23,42,0.25)",
                zIndex: 2,
              }}>
                <span style={{ color: hovered.cfg?.color, fontWeight: 600 }}>{hovered.cfg?.label}</span>
                {" · "}
                {formatDuration(Math.round((hovered.seg.endMs - hovered.seg.startMs) / 1000))}
                {" · "}
                {formatTime(hovered.seg.startMs, true)} – {formatTime(hovered.seg.endMs, true)}
              </div>
            )}
          </div>

          {/* Tick labels along the x-axis, zoom-aware */}
          {hasData && ticks.length > 0 && (
            <div style={{ position: "relative", height: 18, marginTop: 4 }}>
              {ticks.map(tick => (
                <span key={`label-${tick.ms}`} style={{
                  position: "absolute", left: `${tick.pct}%`, transform: "translateX(-50%)",
                  fontSize: 10, color: "#94a3b8", whiteSpace: "nowrap",
                }}>
                  {tick.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Full sleep window start/end (unzoomed) */}
      {hasData && (
        <div style={{
          display: "flex", justifyContent: "space-between",
          padding: `4px ${CHART_LABEL_PAD}px 0 ${YAXIS_WIDTH + CHART_LABEL_PAD}px`,
          fontSize: 11, color: "#64748b",
        }}>
          <span>{formatTime(viewStart)}</span>
          <span>{formatTime(viewEnd)}</span>
        </div>
      )}

      {/* ── Legend ── */}
      {hasData && (
        <div style={{
          display: "flex", justifyContent: "center", gap: 16,
          padding: "12px 10px 4px", flexWrap: "wrap",
        }}>
          {STAGE_ORDER.map(stage => {
            const cfg = STAGE_CFG[stage];
            return (
              <div key={stage} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{
                  width: 10, height: 10, borderRadius: 3,
                  background: cfg.color, display: "inline-block",
                }} />
                <span style={{ fontSize: 11, color: "#475569" }}>{cfg.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}